import { useEffect, useState } from "react";
import { RegisterCard } from "../components/RegisterCard";
import { TopAdminNav } from "../layouts/TopAdminNav";
import "../assets/css/main.css";
import { defaultUrl } from "../features/actions";
import toast from "react-hot-toast";
import Pagination from "../components/pagination";
import { ContactType, IPaginationParams } from "../types/interfaces";
import { makeFetchRequest } from "../utilities/common";
import { useDebounce } from "../hooks/useDebounce";
import {
  Users,
  Search,
  X,
  Phone,
  Mail,
  AlertCircle,
  BookOpen,
} from "lucide-react";

export const Contact = () => {
  const [contactList, setContactList] = useState<ContactType[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPage, setTotalPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const [paginationParams, setPaginationParams] = useState<IPaginationParams>({
    page: 1,
    totalPages: 1,
    totalItems: 0,
    from: 0,
    to: 0,
  });

  async function fetchContacts() {
    setIsLoading(true);
    try {
      const cleanSearch = debouncedSearchTerm ? debouncedSearchTerm.trim() : "";
      const data = await makeFetchRequest(
        `${defaultUrl}/students?q=${cleanSearch}&page=${page}&per_page=15`,
        true,
        true
      );

      const meta = data?.data?.meta || data?.meta;
      const rawList = data?.data?.data || data?.data || [];

      setPaginationParams({
        page,
        totalPages: meta?.last_page || 1,
        from: meta?.from || (rawList.length ? (page - 1) * 15 + 1 : 0),
        to:
          meta?.to ||
          (rawList.length ? Math.min(page * 15, meta?.total || rawList.length) : 0),
        totalItems: meta?.total !== undefined ? meta.total : rawList.length,
      });

      const students = Array.isArray(rawList)
        ? rawList.map((student: any) => {
            const name = [student?.firstname, student?.othername, student?.surname]
              .filter(Boolean)
              .join(" ")
              .trim() || "Student";
            return {
              ...student,
              name,
              raw_picture: student?.profile_picture,
            };
          })
        : [];

      const sortByName = students.sort((a: any, b: any) =>
        (a?.name || "").localeCompare(b?.name || "")
      );

      setTotalPage(meta?.last_page || 1);
      setContactList(sortByName);
    } catch (err: any) {
      console.error("Failed to fetch contacts:", err);
      toast.error("Failed to fetch contacts. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line
  }, [debouncedSearchTerm, page]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top Navigation Row */}
      <TopAdminNav name="Jane Doe" title="Communications Registry" />

      <div className="max-w-[1400px] mx-auto p-4 md:p-8 space-y-6 animate-in fade-in duration-300">
        {/* Overview Context Card Block */}
        <RegisterCard
          title="Contacts & Leads Directory"
          details="Access and oversee verified profiles for parents, urgent guardians, and legal emergency links."
        />

        {/* Main Content Card Container */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm flex flex-col overflow-hidden">
          {/* Table Operations Header Panel */}
          <div className="p-6 border-b border-slate-100 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl bg-blue-50 text-[#0096D1] flex items-center justify-center border border-blue-100 shadow-xs shrink-0">
                <BookOpen size={20} />
              </div>
              <div>
                <h2 className="text-base font-extrabold text-slate-800 tracking-tight">
                  Parental Liaison Registry
                </h2>
                <p className="text-xs font-semibold text-slate-400">
                  {paginationParams.totalItems !== undefined
                    ? `Displaying ${paginationParams.from || 0} - ${
                        paginationParams.to || 0
                      } of ${paginationParams.totalItems} registered pupils`
                    : "Access verified student and parent contact channels"}
                </p>
              </div>
            </div>

            {/* Clean Integrated Search Bar */}
            <div className="w-full sm:w-80 relative group">
              <Search
                size={15}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#0096D1] transition-colors"
              />
              <input
                id="searchAttendanceInput"
                type="text"
                placeholder="Search pupil or parent name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#F8FAFC] border border-slate-200/90 rounded-2xl pl-10 pr-9 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-[#0096D1] focus:bg-white focus:ring-2 focus:ring-[#0096D1]/10 transition-all placeholder:text-slate-400"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Responsive Table Area */}
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left border-collapse min-w-[850px]">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] font-black text-slate-400 uppercase tracking-widest bg-slate-50/60">
                  <th className="py-4 px-6 w-[32%]">Pupil Identity</th>
                  <th className="py-4 px-6 w-[32%]">Primary Sponsor Contact</th>
                  <th className="py-4 px-6 w-[26%]">Backup Secondary Contact</th>
                  <th className="py-4 px-6 w-[10%] text-right">Quick Reach</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {isLoading ? (
                  <tr>
                    <td colSpan={4} className="py-20 text-center">
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0096D1]"></div>
                        <p className="text-xs font-bold text-slate-400">
                          Loading communications records...
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : contactList.length > 0 ? (
                  contactList.map((item: any, index: number) => {
                    const primary = item.parents?.[0];
                    const secondary = item.parents?.[1];

                    return (
                      <tr
                        key={item.id || index}
                        className="hover:bg-slate-50/70 transition-colors duration-150 group"
                      >
                        {/* Pupil Profile */}
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="relative w-11 h-11 shrink-0">
                              {item.raw_picture ? (
                                <img
                                  src={item.raw_picture}
                                  alt={item.name}
                                  className="w-11 h-11 rounded-full object-cover border border-slate-200 shadow-xs"
                                  onError={(e) => {
                                    (e.currentTarget as HTMLElement).style.display =
                                      "none";
                                    const fallback =
                                      e.currentTarget.parentElement?.querySelector(
                                        ".pupil-fallback"
                                      );
                                    if (fallback)
                                      (fallback as HTMLElement).style.display =
                                        "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                className={`pupil-fallback w-11 h-11 rounded-full bg-blue-50 text-blue-700 font-black text-xs items-center justify-center border border-blue-200 shadow-xs ${
                                  item.raw_picture ? "hidden" : "flex"
                                }`}
                              >
                                {item.firstname?.[0]?.toUpperCase() || ""}
                                {item.surname?.[0]?.toUpperCase() || ""}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="font-extrabold text-[13.5px] text-slate-800 tracking-tight group-hover:text-[#0096D1] transition-colors truncate">
                                {item.name}
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {item.classroom?.name && (
                                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200/60">
                                    {item.classroom.name}
                                  </span>
                                )}
                                <span className="text-[10px] font-mono font-medium text-slate-400">
                                  #{item.id?.substring(0, 6) || "N/A"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Primary Sponsor */}
                        <td className="py-4 px-6">
                          {primary ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-extrabold text-[13px] text-slate-800 capitalize">
                                  {primary.name}
                                </span>
                                {primary.relationship && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    {primary.relationship}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-col gap-0.5 text-xs">
                                {primary.phone && (
                                  <a
                                    href={`tel:${primary.phone}`}
                                    className="inline-flex items-center gap-1.5 font-semibold text-slate-600 hover:text-emerald-700 transition-colors"
                                  >
                                    <Phone
                                      size={12}
                                      className="text-slate-400 shrink-0"
                                    />
                                    <span>{primary.phone}</span>
                                  </a>
                                )}
                                {primary.email && (
                                  <a
                                    href={`mailto:${primary.email}`}
                                    className="inline-flex items-center gap-1.5 text-slate-500 hover:text-emerald-700 transition-colors truncate max-w-[240px]"
                                    title={primary.email}
                                  >
                                    <Mail
                                      size={12}
                                      className="text-slate-400 shrink-0"
                                    />
                                    <span className="truncate">
                                      {primary.email}
                                    </span>
                                  </a>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 italic bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100">
                              <AlertCircle
                                size={13}
                                className="text-slate-300"
                              />
                              <span>No primary sponsor</span>
                            </div>
                          )}
                        </td>

                        {/* Backup Secondary Contact */}
                        <td className="py-4 px-6">
                          {secondary ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-extrabold text-[13px] text-slate-800 capitalize">
                                  {secondary.name}
                                </span>
                                {secondary.relationship && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-50 text-purple-700 border border-purple-200">
                                    {secondary.relationship}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-col gap-0.5 text-xs">
                                {secondary.phone && (
                                  <a
                                    href={`tel:${secondary.phone}`}
                                    className="inline-flex items-center gap-1.5 font-semibold text-slate-600 hover:text-purple-700 transition-colors"
                                  >
                                    <Phone
                                      size={12}
                                      className="text-slate-400 shrink-0"
                                    />
                                    <span>{secondary.phone}</span>
                                  </a>
                                )}
                                {secondary.email && (
                                  <a
                                    href={`mailto:${secondary.email}`}
                                    className="inline-flex items-center gap-1.5 text-slate-500 hover:text-purple-700 transition-colors truncate max-w-[240px]"
                                    title={secondary.email}
                                  >
                                    <Mail
                                      size={12}
                                      className="text-slate-400 shrink-0"
                                    />
                                    <span className="truncate">
                                      {secondary.email}
                                    </span>
                                  </a>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 font-medium italic">
                              — None listed —
                            </span>
                          )}
                        </td>

                        {/* Quick Reach Actions */}
                        <td className="py-4 px-6 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5">
                            {primary?.phone && (
                              <a
                                href={`tel:${primary.phone}`}
                                title={`Call ${
                                  primary.name || "Primary Sponsor"
                                }`}
                                className="p-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-all border border-slate-200/60 shadow-2xs"
                              >
                                <Phone size={13} />
                              </a>
                            )}
                            {primary?.email && (
                              <a
                                href={`mailto:${primary.email}`}
                                title={`Email ${
                                  primary.name || "Primary Sponsor"
                                }`}
                                className="p-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 transition-all border border-slate-200/60 shadow-2xs"
                              >
                                <Mail size={13} />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={4} className="py-20 text-center">
                      <div className="flex flex-col items-center justify-center max-w-sm mx-auto">
                        <div className="w-12 h-12 rounded-2xl bg-slate-50 text-slate-300 flex items-center justify-center mb-3 border border-slate-100">
                          <Users size={24} />
                        </div>
                        <h4 className="text-sm font-black text-slate-700 mb-1">
                          No Contacts Found
                        </h4>
                        <p className="text-xs text-slate-400 font-medium">
                          {searchTerm
                            ? `No pupil or parent records match "${searchTerm}".`
                            : "No contact records available."}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls Footer Container */}
          <div className="p-4 border-t border-slate-100 bg-white">
            <Pagination
              page={page}
              totalPages={totalPage}
              setPage={setPage}
              paginationParams={paginationParams}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
